import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useEffect, useRef } from "react";

export type SearchBarProps = {
    testId?: string;
    className?: string;
    placeholder?: string;
    hotkeyLabel?: string;
    value?: string;
    onChange?: (value: string) => void;
};

export const SearchBar = ({
    testId,
    className,
    placeholder = "Search Memo",
    hotkeyLabel = "⌘K",
    value,
    onChange,
}: SearchBarProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const hasValue = value && value.length > 0;

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key === "k") {
                event.preventDefault();
                inputRef.current?.focus();
            }
            if (
                event.key === "k" &&
                !event.metaKey &&
                !event.ctrlKey &&
                document.activeElement?.tagName !== "INPUT" &&
                document.activeElement?.tagName !== "TEXTAREA"
            ) {
                event.preventDefault();
                inputRef.current?.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    const handleClear = () => {
        onChange?.("");
        inputRef.current?.focus();
    };

    return (
        <div className={clsx("relative my-2", className)} data-testid={testId}>
            <MagnifyingGlassIcon
                stroke="#9CA3AF"
                strokeWidth={1.5}
                className={clsx(
                    "-translate-y-1/2",
                    "pointer-events-none absolute left-3 top-1/2 size-4 text-gray-400",
                )}
            />
            <input
                ref={inputRef}
                type="search"
                name="search-sidebar"
                aria-label={placeholder}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                className={clsx(
                    "border-default bg-default text-default flex border",
                    "placeholder:text-gray-400",
                    "body-s my-0 w-full rounded-lg py-2.5 pl-9 pr-16 focus:ring-0 focus:ring-offset-0",
                    "focus:bg-default outline-none focus:border-gray-500",
                    "transition-colors duration-150 ease-in-out",
                    "[&::-webkit-search-cancel-button]:appearance-none",
                    "[&::-webkit-search-decoration]:appearance-none",
                    "[&::-ms-clear]:hidden",
                    "[&::-ms-reveal]:hidden",
                )}
            />
            {hasValue ? (
                <div
                    onClick={handleClear}
                    className={clsx(
                        "-translate-y-1/2 bg-gray-500",
                        "absolute right-3 top-1/2 rounded-full p-1",
                        "transition-colors duration-150 ease-in-out",
                        "focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2",
                    )}
                    aria-label="Clear search"
                >
                    <XMarkIcon
                        stroke="#1E293B"
                        className="size-3"
                        strokeWidth={2}
                    />
                </div>
            ) : (
                <span
                    className={clsx(
                        "-translate-y-1/2 bg-gray-500 px-1 py-0.5",
                        "text-bg-default text-slate-800",
                        "body-micro pointer-events-none absolute right-3 top-1/2 rounded-md",
                    )}
                >
                    {hotkeyLabel}
                </span>
            )}
        </div>
    );
};

export default SearchBar;
